/**
 * The wallet's swap record store.
 *
 * Storage moved out of localStorage into `@arkade-os/swap`'s IndexedDB
 * repository, which also backs the restore-scan cursor and the markets cache —
 * one seam instead of three ad hoc keys.
 *
 * A fourth store, `rfqSwaps`, arrived with `DB_VERSION` 2 and is written by
 * `RfqSwapManager` rather than by anything here: `createSwapClient` takes this
 * object as its `repository`, and its manager composes every record itself. So
 * this file enumerates four consumers, only three of which call it directly.
 *
 * That version bump is one-way. A browser at 2 cannot be served a bundle
 * pinning an older `@arkade-os/swap`: the open fails `VersionError` across the
 * WHOLE database, asset swaps and markets cache included. Rolling back this
 * release means rolling back the data, not just the bundle.
 */
import { IndexedDbAssetSwapRepository, type AssetSwap } from '@arkade-os/swap'

/** Shared per tab: the repository opens its database lazily on first use, and
 * a second instance would open a second connection to the same stores. */
export const assetSwapRepository = new IndexedDbAssetSwapRepository()

/** Display facts frozen at quote time — only what the activity UI reads.
 * Every field is optional: a restore can only backfill what is recoverable
 * (feeBps from the market card), and every consumer falls back per-field.
 * TODO: once fee bps rides in a packet inside the funding tx, feeBps stops
 * being a quote-time fact — read it from the tx (creation and restore alike)
 * and drop the field here. */
export interface AssetSwapQuoteSnapshot {
  fromTicker?: string
  fromDecimals?: number
  toTicker?: string
  toDecimals?: number
  feeBps?: number
  fiatCurrency?: string
  fromFiatAmount?: number
}

/** The package's record plus the quote snapshot it deliberately does not own.
 * The repository stores records whole, so `quote` survives package-side writes
 * (`cancelOffer`, the watcher) untouched. */
export type WalletAssetSwap = AssetSwap & { quote?: AssetSwapQuoteSnapshot }

/**
 * Where the snapshot is kept.
 *
 * A store of its own rather than a field on the record, because the record is
 * the client's: `SwapRecord` is rewritten whole on every drive pass, so a
 * wallet-private key on it would survive exactly until the next one. The v1
 * `AssetSwap` tolerated the extra field and the v2 record does not, which is
 * why this moved out.
 */
export const QUOTE_SNAPSHOTS = 'swapQuoteSnapshots'

const readSnapshots = (): Record<string, AssetSwapQuoteSnapshot> => {
  try {
    return JSON.parse(localStorage.getItem(QUOTE_SNAPSHOTS) ?? '{}') as Record<string, AssetSwapQuoteSnapshot>
  } catch {
    return {}
  }
}

/** The snapshot for a swap, or undefined when none was stored. */
export const quoteSnapshotOf = (id: string): AssetSwapQuoteSnapshot | undefined => readSnapshots()[id]

export const saveQuoteSnapshot = (id: string, quote: AssetSwapQuoteSnapshot): void => {
  localStorage.setItem(QUOTE_SNAPSHOTS, JSON.stringify({ ...readSnapshots(), [id]: quote }))
}
